import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { BankStore } from '../../core/stores/base.store';
import { FuseConfirmationService } from '../../core/services/confirmation/confirmation.service';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-add-bank',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatToolbarModule,
    MatCheckboxModule,
    NumberFormatDirective,
    MatCardModule
  ],
  templateUrl: './add-bank.component.html',
  styleUrls: ['./add-bank.component.css']
})
export class AddBankComponent implements OnInit {
  bankForm: FormGroup;
  isEdit = false;
  
  states = [
    'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN',
    'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ',
    'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT',
    'WA', 'WI', 'WV', 'WY'
  ];
  
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private bankStore = inject(BankStore);
  #fuseConfirmationService = inject(FuseConfirmationService);

  constructor() {
    this.bankForm = this.fb.group({
      id: [null],
      user_id: [JSON.parse(localStorage.getItem('user_profile')).id],
      name: ['', Validators.required],
      starting_balance: [null, [Validators.min(0)]],
      loc: [false],
      loc_interest_rate: [2, [Validators.min(0), Validators.max(100)]],
      loc_balance: [0],
      loc_interest: [0],
      loc_total_interest: [0]
    });

    // Subscribe to changes in the loc checkbox to enable/disable LOC related fields
    this.bankForm.get('loc')?.valueChanges.subscribe(hasLoc => {
      const locInterestRateControl = this.bankForm.get('loc_interest_rate');
      const locTotalInterestControl = this.bankForm.get('loc_total_interest');
      
      if (hasLoc) {
        // Enable and set validators for interest rate
        locInterestRateControl?.enable();
        locInterestRateControl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
        
        // Enable total interest field
        locTotalInterestControl?.enable();
        locTotalInterestControl?.setValidators([Validators.min(0)]);
      } else {
        // Disable and reset interest rate
        locInterestRateControl?.disable();
        locInterestRateControl?.clearValidators();
        locInterestRateControl?.setValidators([Validators.min(0), Validators.max(100)]);
        locInterestRateControl?.setValue(null);
        
        // Disable and reset total interest
        locTotalInterestControl?.disable();
        locTotalInterestControl?.setValue(null);
      }
      
      // Update validity for both controls
      locInterestRateControl?.updateValueAndValidity();
      locTotalInterestControl?.updateValueAndValidity();
    });
  }

  ngOnInit() {
    if (this.bankStore.selected()) {
      this.isEdit = true;
      if ( this.bankStore.selected()) {
        this.bankForm.patchValue( this.bankStore.selected());
      }
    }
  }

  onSubmit() {
    if (this.bankForm.valid) {
      const formValue = this.bankForm.value;
      // Safely convert string values to numbers
      if (formValue.starting_balance) {
        formValue.starting_balance = Number(formValue.starting_balance.toString().replace(/,/g, ''));
      }
      if (this.isEdit) {
        this.bankStore.update(formValue);
      } else {
        delete formValue.id;
        this.bankStore.create({
          entity: formValue,
          endpoint: 'bank'
        });
      }
      this.router.navigate(['/banks']);
    }
  }

  delete() {
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Bank',
      message: 'Are you sure you want to delete this bank? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.bankStore.delete(this.bankStore.selected());
      }
    })
    this.router.navigate(['/banks']);
  }

  addCommas(input: HTMLInputElement) {
    let value = input.value.replace(/,/g, ''); // Remove existing commas
    if (value) {
      value = Number(value).toLocaleString(); // Add commas
    }
    input.value = value;
  }

  onCancel() {
    this.router.navigate(['/banks']);
  }
}
